import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Link } from 'react-router-dom';
import subscriptionAPI from '../api/subscriptionAPI';
import LoadingSpinner from '../components/LoadingSpinner';
import './MySubscriptions.css';

const MySubscriptions = () => {
  const { user } = useAuth();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [pagination, setPagination] = useState({});

  useEffect(() => {
    fetchSubscriptions();
  }, [filter]);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const params = filter !== 'all' ? { status: filter } : {};
      const response = await subscriptionAPI.getUserSubscriptions(params);
      
      if (response.success) {
        setSubscriptions(response.subscriptions);
        setPagination(response.pagination || {});
      } else {
        setError(response.message || 'Failed to fetch subscriptions');
      }
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      setError('Failed to fetch subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const handlePauseSubscription = async (subscriptionId) => {
    try {
      const reason = prompt('Please provide a reason for pausing:');
      if (!reason) return;

      const startDate = new Date().toISOString().split('T')[0];
      const endDate = prompt('Pause until (YYYY-MM-DD):');
      if (!endDate) return;

      const response = await subscriptionAPI.pauseSubscription(subscriptionId, {
        reason,
        startDate,
        endDate
      });

      if (response.success) {
        alert('Subscription paused successfully');
        fetchSubscriptions();
      } else {
        alert(response.message || 'Failed to pause subscription');
      }
    } catch (error) {
      console.error('Error pausing subscription:', error);
      alert('Failed to pause subscription');
    }
  };

  const handleResumeSubscription = async (subscriptionId) => {
    try {
      const response = await subscriptionAPI.resumeSubscription(subscriptionId);

      if (response.success) {
        alert('Subscription resumed successfully');
        fetchSubscriptions();
      } else {
        alert(response.message || 'Failed to resume subscription');
      }
    } catch (error) {
      console.error('Error resuming subscription:', error);
      alert('Failed to resume subscription');
    }
  };

  const handleCancelSubscription = async (subscriptionId) => {
    try {
      const reason = prompt('Please provide a reason for cancellation:');
      if (!reason) return;

      if (!window.confirm('Are you sure you want to cancel this subscription?')) {
        return;
      }

      const response = await subscriptionAPI.cancelSubscription(subscriptionId, { reason });

      if (response.success) {
        alert('Subscription cancelled successfully');
        fetchSubscriptions();
      } else {
        alert(response.message || 'Failed to cancel subscription');
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      alert('Failed to cancel subscription');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return '#28a745';
      case 'pending': return '#ffc107';
      case 'paused': return '#fd7e14';
      case 'cancelled': return '#dc3545';
      case 'completed': return '#6c757d';
      case 'expired': return '#dc3545';
      default: return '#6c757d';
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="my-subscriptions">
      <div className="container">
        <div className="header-section">
          <h1>My Subscriptions</h1>
          <p>Manage your monthly service subscriptions</p>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="filters">
          <button 
            className={filter === 'all' ? 'active' : ''} 
            onClick={() => setFilter('all')}
          >
            All
          </button>
          <button 
            className={filter === 'active' ? 'active' : ''} 
            onClick={() => setFilter('active')}
          >
            Active
          </button>
          <button 
            className={filter === 'pending' ? 'active' : ''} 
            onClick={() => setFilter('pending')}
          >
            Pending
          </button>
          <button 
            className={filter === 'paused' ? 'active' : ''} 
            onClick={() => setFilter('paused')}
          >
            Paused
          </button>
          <button 
            className={filter === 'cancelled' ? 'active' : ''} 
            onClick={() => setFilter('cancelled')}
          >
            Cancelled
          </button>
        </div>

        {subscriptions.length === 0 ? (
          <div className="no-subscriptions">
            <div className="empty-state">
              <h3>No subscriptions found</h3>
              <p>You haven't created any subscription plans yet.</p>
              <Link to="/helpers" className="btn btn-primary">Create Your First Subscription</Link>
            </div>
          </div>
        ) : (
          <div className="subscriptions-list">
            {subscriptions.map((subscription) => (
              <div key={subscription._id} className="subscription-card">
                <div className="subscription-header">
                  <div className="subscription-info">
                    <h3>{subscription.helperId.name}</h3>
                    <div className="subscription-meta">
                      <span 
                        className="status-badge"
                        style={{ backgroundColor: getStatusColor(subscription.status) }}
                      >
                        {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
                      </span>
                      <span className="subscription-id">#{subscription._id.slice(-6)}</span>
                    </div>
                  </div>
                  <div className="subscription-amount">
                    <div className="amount">₹{subscription.pricing.totalMonthlyAmount}</div>
                    <div className="period">per month</div>
                  </div>
                </div>

                <div className="subscription-details">
                  <div className="detail-row">
                    <strong>Services:</strong>
                    <div className="services-list">
                      {subscription.services.map((service, index) => (
                        <span key={index} className="service-tag">
                          {service.serviceType.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          {service.serviceOptions?.apartmentType && 
                            ` (${service.serviceOptions.apartmentType.toUpperCase()})`
                          }
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="detail-row">
                    <strong>Schedule:</strong>
                    <div className="schedule-info">
                      <div className="days">
                        {subscription.schedule.daysOfWeek.map(day => 
                          day.charAt(0).toUpperCase() + day.slice(1)
                        ).join(', ')}
                      </div>
                      <div className="time-slots">
                        {subscription.schedule.timeSlots.map((slot, index) => (
                          <span key={index} className="time-slot">
                            {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                            {slot.applicableServices.length > 0 && (
                              <small> ({slot.applicableServices.join(', ')})</small>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="detail-row">
                    <strong>Duration:</strong>
                    <span>
                      {formatDate(subscription.subscriptionPeriod.startDate)} - {formatDate(subscription.subscriptionPeriod.endDate)}
                      {subscription.daysRemaining > 0 && (
                        <small> ({subscription.daysRemaining} days remaining)</small>
                      )}
                    </span>
                  </div>

                  <div className="detail-row">
                    <strong>Progress:</strong>
                    <div className="progress-info">
                      <div className="progress-bar">
                        <div 
                          className="progress-fill" 
                          style={{ width: `${subscription.progressPercentage}%` }}
                        ></div>
                      </div>
                      <span>{subscription.servicesCompleted}/{subscription.totalServicesScheduled} services completed</span>
                    </div>
                  </div>

                  {subscription.customerAddress && (
                    <div className="detail-row">
                      <strong>Address:</strong>
                      <span>{subscription.customerAddress}</span>
                    </div>
                  )}
                </div>

                <div className="subscription-actions">
                  {subscription.status === 'active' && (
                    <>
                      <button 
                        className="btn btn-secondary"
                        onClick={() => handlePauseSubscription(subscription._id)}
                      >
                        Pause
                      </button>
                      <button 
                        className="btn btn-danger"
                        onClick={() => handleCancelSubscription(subscription._id)}
                      >
                        Cancel
                      </button>
                    </>
                  )}
                  
                  {subscription.status === 'paused' && (
                    <>
                      <button 
                        className="btn btn-primary"
                        onClick={() => handleResumeSubscription(subscription._id)}
                      >
                        Resume
                      </button>
                      <button 
                        className="btn btn-danger"
                        onClick={() => handleCancelSubscription(subscription._id)}
                      >
                        Cancel
                      </button>
                    </>
                  )}

                  {subscription.status === 'pending' && subscription.payment.status === 'pending' && (
                    <button className="btn btn-primary">
                      Complete Payment
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {pagination.pages > 1 && (
          <div className="pagination">
            <span>Page {pagination.page} of {pagination.pages}</span>
            <span>{pagination.total} total subscriptions</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default MySubscriptions; 